// Port of Flutter's devicesScreen.dart — a room's devices.
//
//   AppBar  avatar · room name · location name
//   body    the same common-devices strip as the Rooms screen, then the SENSOR
//           strip, then the room's device grid (maxCrossAxisExtent 250, aspect
//           0.9, spacing 10)
//   FAB     Add Device
//
// Like the Dart, opening a device subscribes its `<uno>/client` topic first so
// live status is already flowing by the time the control screen builds.
//
// ⚠️ TWO THINGS THE LIST DOES BEFORE IT DRAWS, and both were missing:
//
//   sort    every row carries the customer's own `SortOrder`; the desktop shell
//           sorted by it and the phone did not, so the same room read in two
//           different orders. Worse on this screen than most, because the room
//           reply is COMPLETED from the location reply (core/devices/roomMerge)
//           and those rows are appended — a merged-in device landed last
//           whatever its SortOrder said.
//   split   sensors have no outputs and belong in their own row above the grid;
//           see core/devices/sensorState.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import WifiIcon from '@mui/icons-material/Wifi';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ProfileTitleBar } from '../components/MobileAppBar';
import CommonDevicesStrip from '../components/CommonDevicesStrip';
import SensorStrip from '../components/SensorStrip';
import DeviceCard from '../components/DeviceCard';
import ExtentGrid from '../components/ExtentGrid';
import CircleMenu from '../components/CircleMenu';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';
import { useApp } from '../../../core/state/AppContext';
import { getDevicesByRoom, getCommonDevices } from '../../../core/api/api';
import { sortByOrder } from '../../../core/utils/sortOrder';
import { applyDeviceOrder } from '../../../core/utils/orderCache';
import { splitSensors } from '../../../core/devices/sensorState';
import { subscribeTopic } from '../../../core/mqtt/mqttClient';
import { drillFromDevices, arrivedViaBack, parentOf } from '../../../core/nav/appNav';

export default function MobileDevicesScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { state } = useLocation();
  const { userId, toggleFavourite, canUpdateFirmware } = useApp();

  const [devices, setDevices] = useState(null);
  const [common, setCommon] = useState([]);
  const [updateOpen, setUpdateOpen] = useState(false);

  const roomName = state?.roomName || 'Room';
  const locationName = state?.locationName || '';
  const locationId = state?.locationId;

  const load = useCallback(async () => {
    if (!userId) return;
    const [ds, cs] = await Promise.all([
      getDevicesByRoom(userId, roomId, locationId).catch(() => null),
      locationId ? getCommonDevices(userId, locationId).catch(() => null) : Promise.resolve(null),
    ]);
    // Backend SortOrder first, then the local reorder override — the same two
    // steps, in the same order, as DesktopShell. A drag done in a wide window
    // has to survive the window being narrowed into the mobile shell.
    const rows = sortByOrder(Array.isArray(ds?.data) ? ds.data : []);
    setDevices(applyDeviceOrder(userId, roomId, rows, (d) => d.UniqueNumber));
    setCommon(sortByOrder(Array.isArray(cs?.data) ? cs.data : []));
  }, [userId, roomId, locationId]);

  useEffect(() => { load(); }, [load]);

  // Sensors out of the grid and into their own row. `devices` stays null while
  // loading, and null is not an empty room — the grid below tells them apart.
  const { sensors, controls } = useMemo(
    () => splitSensors(devices || []),
    [devices],
  );
  const commonSplit = useMemo(() => splitSensors(common), [common]);
  // One SENSORS row, not two: a location's roomless sensors and this room's own
  // read the same to the person looking at them.
  const allSensors = useMemo(
    () => [...commonSplit.sensors, ...sensors],
    [commonSplit.sensors, sensors],
  );

  // ── One device: show it, not a grid of one ──────────────────────────────
  //
  // The end of the drill. `/device/:uno` renders whatever the DeviceID calls
  // for, so a lone DeviceID 49 lands on the four-touch switches with nothing
  // else to tap through — which is the whole point of the chain.
  //
  // ⚠️ The common strip above the grid counts too: one device in this room plus
  // one common device at this location is two things on the screen.
  //
  // Not on a back, and not before the lists have landed — see RoomsScreen.
  const drilled = useRef(false);
  useEffect(() => {
    if (devices == null || drilled.current || arrivedViaBack(state)) return;
    const target = drillFromDevices({
      devices, common, roomId, roomName, locationId, locationName,
    });
    if (!target) return;
    drilled.current = true;
    subscribeTopic(`${target.state.device.UniqueNumber}/client`);
    navigate(target.path, { replace: true, state: target.state });
  }, [devices, common, state, navigate, roomId, roomName, locationId, locationName]);

  const goBack = () => {
    const t = parentOf(`/room/${roomId}/devices`, { locationId, locationName });
    if (t) navigate(t.path, { state: t.state });
    else navigate(-1);
  };

  const openDevice = (d) => {
    subscribeTopic(`${d.UniqueNumber}/client`);
    // `from` is what lets the device screen come BACK here correctly: the room
    // row it is handed carries RoomID but no LocationID, and the room list needs
    // the location or it drops device types. See core/devices/deviceNav.js.
    navigate(`/device/${d.UniqueNumber}`, {
      state: { device: d, from: { roomId, roomName, locationId, locationName } },
    });
  };
  const onStar = async (d, fav) => { await toggleFavourite(d, fav); load(); };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      {/* Structural, so the arrow and the Android button agree — and so a room
          that was drilled into still has somewhere to go back to. */}
      <ProfileTitleBar title={roomName} subtitle={locationName} onBack={goBack} />

      {/* PINNED — same two-container split as the Rooms screen: the common strip
          stays put and scrolls sideways, only the device grid scrolls down. */}
      <CommonDevicesStrip
        devices={commonSplit.controls}
        onOpen={openDevice}
        onToggleFavourite={onStar}
      />

      {/* Directly after the common devices, and before the grid. */}
      <SensorStrip devices={allSensors} onOpen={openDevice} onToggleFavourite={onStar} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box sx={{ p: 1 }}>
          {/* `devices == null` is still loading; empty is empty. A room that is
              nothing but sensors is NOT empty — "No Devices Found" printed above
              a row of them reads as a fault. */}
          {devices != null && controls.length === 0 && allSensors.length === 0 && (
            <Typography sx={{ color: c.text, textAlign: 'center' }}>No Devices Found</Typography>
          )}
          {controls.length > 0 && (
            <ExtentGrid maxExtent={250} childAspectRatio={0.9} gap={10}>
              {controls.map((d) => (
                <DeviceCard
                  key={d.UniqueNumber}
                  device={d}
                  onOpen={openDevice}
                  onToggleFavourite={onStar}
                />
              ))}
            </ExtentGrid>
          )}
        </Box>
      </Box>

      <CircleMenu
        items={[
          {
            icon: <AddIcon />,
            title: 'Add Device',
            onClick: () => navigate(`/add-device?locationId=${locationId ?? ''}&roomId=${roomId}`),
          },
          // Onboard a brand-new device: find its setup hotspot and hand it the
          // Wi-Fi it should join. The location travels along so the picker can
          // offer the networks already in use at this site.
          {
            icon: <WifiIcon />,
            title: 'Set Up New Device',
            onClick: () => navigate(`/wifi-setup?locationId=${locationId ?? ''}`),
          },
          // Room-wide firmware push — owner / super admin only.
          ...(canUpdateFirmware ? [{
            icon: <SystemUpdateAltIcon />,
            title: 'Update Code (Room)',
            onClick: () => setUpdateOpen(true),
          }] : []),
        ]}
      />

      <UpdateCodeDialog
        open={updateOpen}
        scope={{ type: 'room', id: roomId, name: roomName, locationId }}
        onClose={() => setUpdateOpen(false)}
      />
    </Box>
  );
}
